import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Camera, Loader2, Trash2, Upload, X } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import { toast } from 'sonner';
import { progressiveCompress } from '../../utils/imageCompression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Per-animal multi-photo gallery.
 *
 * Props:
 *  - animalId (string, required): UUID of the animal/herd/flock
 *  - animalKind (string, required): 'foundation' | 'individual' | 'herd' | 'flock'
 *  - animalName (string): denormalized for the gallery view
 *  - animalType (string): goat/sheep/cattle/poultry — for global gallery filtering
 *  - className (string, optional)
 */
const AnimalPhotoGallery = ({ animalId, animalKind, animalName, animalType, className }) => {
  const { getAuthHeader } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const loadPhotos = async () => {
    if (!animalId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/animal-photos?animal_id=${animalId}`, {
        headers: getAuthHeader(),
      });
      setPhotos(res.data.photos || []);
    } catch (e) {
      console.error('Failed to load photos:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!animalId) return;
      try {
        const res = await axios.get(`${API_URL}/api/animal-photos?animal_id=${animalId}`, {
          headers: getAuthHeader(),
        });
        if (cancelled) return;
        setPhotos(res.data.photos || []);
      } catch (e) {
        console.error('Failed to load photos:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [animalId, getAuthHeader]);

  const handleAdd = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      // Compress before upload
      const compressed = await progressiveCompress(file);
      const fd = new FormData();
      fd.append('file', compressed);
      const upRes = await axios.post(`${API_URL}/api/upload/photo?category=${animalKind}`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const url = upRes.data.url.startsWith('http') ? upRes.data.url : `${API_URL}${upRes.data.url}`;

      await axios.post(`${API_URL}/api/animal-photos`, {
        animal_id: animalId,
        animal_kind: animalKind,
        animal_name: animalName,
        animal_type: animalType,
        photo_url: url,
      }, { headers: getAuthHeader() });

      toast.success('Photo added');
      loadPhotos();
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API_URL}/api/animal-photos/${photoId}`, { headers: getAuthHeader() });
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      setLightboxIndex(null);
      toast.success('Photo deleted');
    } catch (err) {
      console.error(err);
      toast.error('Delete failed');
    }
  };

  return (
    <div className={className} data-testid="animal-photo-gallery">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Photos</span>
          <span className="text-xs text-muted-foreground">({photos.length})</span>
        </div>
        <label
          htmlFor={`gallery-upload-${animalId}`}
          className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? 'Uploading…' : 'Add photo'}
          <input
            id={`gallery-upload-${animalId}`}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={uploading}
            onChange={handleAdd}
            data-testid="gallery-upload-input"
          />
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-6 px-3 rounded-lg border border-dashed border-muted-foreground/30 text-xs text-muted-foreground">
          No photos yet. Tap &quot;Add photo&quot; to start building a gallery for {animalName || 'this animal'}.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p, idx) => (
            <button
              key={p.id}
              onClick={() => setLightboxIndex(idx)}
              className="relative aspect-square rounded-lg overflow-hidden border bg-muted hover:opacity-90 transition-opacity"
              data-testid={`gallery-thumb-${p.id}`}
            >
              <img src={p.photo_url} alt={p.caption || ''} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={lightboxIndex !== null} onOpenChange={(o) => !o && setLightboxIndex(null)}>
        <DialogContent className="max-w-3xl p-0 bg-black/95 border-0">
          <DialogTitle className="sr-only">Photo preview</DialogTitle>
          {lightboxIndex !== null && photos[lightboxIndex] && (
            <div className="relative">
              <button
                onClick={() => setLightboxIndex(null)}
                className="absolute top-2 right-2 z-10 p-2 rounded-full bg-black/60 text-white hover:bg-black/80"
                data-testid="gallery-close-lightbox"
              >
                <X className="w-4 h-4" />
              </button>
              <img
                src={photos[lightboxIndex].photo_url}
                alt={photos[lightboxIndex].caption || ''}
                className="w-full max-h-[80vh] object-contain bg-black"
              />
              <div className="flex items-center justify-between gap-2 p-3 bg-black/80 text-white">
                <div className="text-xs">
                  {photos[lightboxIndex].caption || photos[lightboxIndex].animal_name || ''}
                  {photos[lightboxIndex].created_at && (
                    <span className="ml-2 opacity-70">
                      {new Date(photos[lightboxIndex].created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(photos[lightboxIndex].id)}
                  data-testid="gallery-delete-photo"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnimalPhotoGallery;
